/**
 * Settings, Assistant: on or off (takes effect now), the autonomy mode, where and with what it
 * runs, its limits, and a rule per capability. Owners and admins change them; everyone else
 * reads them. Everything but the switch is saved together from the bar that shows when
 * something changed.
 */
import { Bot, PlugZap, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { AI_CAPABILITIES, AUTONOMY_DESCRIPTION, AUTONOMY_LABEL, AUTONOMY_MODES, CAPABILITY_TIERS, TIER_LABEL } from '@orgus/protocol';
import type { AssistantSettings, AutonomyMode, CapabilityRule } from '@orgus/protocol';
import { Badge, Button, ErrorState, Input, Label, Panel, RadioGroup, RadioGroupItem, Segmented, Select, Skeleton, Switch, Tooltip, cn, formatInt, formatMoney, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useMachines } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { TIER_TONE } from '../ai/model.ts';
import { AI_MODELS } from '../requirements/AiRunOptions.tsx';
import { saveAssistantSettings, useAssistant } from './data.ts';
import { RULE_CHOICES, RULE_CHOICE_LABEL, VERDICT_LABEL, assistantAccountOptions, defaultAccountLine, formToPatch, ruleGroups, sameRules, settingsToForm, withRule } from './model.ts';
import { useLaunchAccounts } from '../launch-accounts/data.ts';
import type { RuleChoice, RuleRow, SettingsErrors, SettingsForm } from './model.ts';
import { ModuleAbsent } from '../server/ModuleAbsent.tsx';
import { useCloud } from '../cloud/data.ts';
import { demoRunner, runnerLine, runnerStatus } from './runner.ts';

type SwitchField = 'enabled' | 'harness';

const SWITCH_COPY: Record<SwitchField, { label: string; on: string; off: string; toastOn: string; toastOff: string }> = {
  enabled: {
    label: 'Let the assistant answer',
    on: 'It answers in Assistant and can act within the mode below. Turning it off stops nothing that already ran; past conversations stay readable.',
    off: 'It answers nothing until you turn it on. Past conversations stay readable.',
    toastOn: 'Assistant turned on',
    toastOff: 'Assistant turned off',
  },
  harness: {
    label: "Use Claude Code's own tools",
    on: 'Files, search, web, subagents and the shell too, each use decided by Orgus like a capability (Claude Code tools, below).',
    off: 'It acts only through Orgus capabilities until you turn this on.',
    toastOn: 'Claude Code tools turned on',
    toastOff: 'Claude Code tools turned off',
  },
};

/** A setting that takes effect the moment it flips (never part of the Save bar). */
function SettingSwitch({ field, settings, canEdit }: { field: SwitchField; settings: AssistantSettings; canEdit: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const copy = SWITCH_COPY[field];
  const value = !!settings[field];
  const flip = async (on: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await saveAssistantSettings({ [field]: on });
      toast(on ? copy.toastOn : copy.toastOff);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <Label className="gap-3 text-ui text-fg">
        <Switch checked={value} onCheckedChange={(v) => void flip(v)} disabled={!canEdit || busy} />
        {copy.label}
      </Label>
      <p className="text-caption text-fg-subtle">{value ? copy.on : copy.off}</p>
      {error && <ErrorState variant="inline" error={error} />}
    </div>
  );
}

function ModeCards({ value, onChange, disabled, ownerOnlyLocked }: { value: AutonomyMode; onChange: (m: AutonomyMode) => void; disabled: boolean; ownerOnlyLocked: boolean }) {
  return (
    <RadioGroup value={value} onValueChange={(v) => onChange(v as AutonomyMode)} aria-label="Autonomy" disabled={disabled} className="gap-2">
      {AUTONOMY_MODES.map((m) => {
        const id = `assistant-mode-${m}`;
        return (
          <label
            key={m}
            htmlFor={id}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-card border px-4 py-3 hover:bg-hover',
              value === m ? 'border-accent bg-surface' : 'border-border bg-surface',
              disabled && 'cursor-default hover:bg-surface',
            )}
          >
            <span className="flex h-5 shrink-0 items-center">
              <RadioGroupItem id={id} value={m} disabled={m === 'full' && ownerOnlyLocked} />
            </span>
            <span className="flex min-w-0 flex-col gap-1">
              <span className="text-ui font-medium text-fg">{AUTONOMY_LABEL[m]}</span>
              <span className="text-caption text-fg-muted">{AUTONOMY_DESCRIPTION[m]}</span>
              {m === 'full' && ownerOnlyLocked && <span className="text-caption text-fg-muted">Only an owner can turn on full autonomy.</span>}
              {m === 'full' && (
                <span className="flex items-center gap-1 text-caption text-status-attention-ink">
                  <TriangleAlert className="size-3 shrink-0" aria-hidden />
                  Critical changes, shell commands included, run without anyone looking. Use it only on machines you can rebuild.
                </span>
              )}
            </span>
          </label>
        );
      })}
    </RadioGroup>
  );
}

function RuleRowView({ row, onChange, disabled }: { row: RuleRow; onChange: (c: RuleChoice) => void; disabled: boolean }) {
  return (
    <li className="flex h-row items-center gap-3 px-4" data-row>
      <Tooltip content={row.description}>
        <span className="flex min-w-0 flex-1 items-baseline gap-2" tabIndex={0}>
          <span className="truncate text-ui text-fg">{row.title}</span>
          <code className="hidden shrink-0 font-mono text-caption text-fg-subtle lg:inline" data-meta>
            {row.name}
          </code>
        </span>
      </Tooltip>
      {row.tool && (
        <Badge tone={TIER_TONE[row.tier]} size="sm" data-chip className="shrink-0">
          {TIER_LABEL[row.tier]}
        </Badge>
      )}
      <Tooltip content={row.why}>
        <span tabIndex={0} className={cn('hidden w-24 shrink-0 text-caption md:inline', row.verdict === 'deny' ? 'text-fg-muted' : 'text-fg-subtle')} data-meta>
          {VERDICT_LABEL[row.verdict]}
        </span>
      </Tooltip>
      <Segmented<RuleChoice>
        size="sm"
        label={row.tool ? `Rule for ${row.title} (${row.name})` : `Rule for ${row.title}`}
        value={row.choice}
        onChange={onChange}
        className="shrink-0"
        options={RULE_CHOICES.map((c) => ({ value: c, label: RULE_CHOICE_LABEL[c], disabled: disabled || (row.readOnly && (c === 'auto' || c === 'ask')) }))}
      />
    </li>
  );
}


const RUN_ORDER: { title: string; line: string }[] = [
  { title: 'A machine online', line: 'Claude Code on one of your machines, on the account below. Installed there first when it is missing.' },
  { title: 'A stopped cloud VM', line: 'With none online, Orgus starts one and the reply waits for it, about a minute.' },
  { title: 'Nothing to run on', line: 'The page says so before anyone types, with Add a machine and Create a cloud VM.' },
];

/** Where replies run, in the order Orgus tries, and what the assistant can do. */
function RunsPanel({ settings }: { settings: AssistantSettings }) {
  const machines = useMachines();
  const cloud = useCloud();
  const now = demoRunner(cloud.mock) ?? runnerStatus(machines, cloud.state, settings.machineId);
  const byTier = CAPABILITY_TIERS.map((t) => ({ t, n: AI_CAPABILITIES.filter((c) => c.tier === t).length }));
  return (
    <Panel title="Where replies run" sub={runnerLine(now)}>
      <div className="flex flex-col gap-4">
        <ol className="grid grid-cols-1 gap-2 sm:grid-cols-3" aria-label="Where replies run, in order">
          {RUN_ORDER.map((r, i) => (
            <li key={r.title} className={cn('flex flex-col gap-1 rounded-card border px-3 py-2', (i === 0 && now.kind === 'ready') || (i === 1 && (now.kind === 'wake' || now.kind === 'starting')) || (i === 2 && now.kind === 'none') ? 'border-accent bg-surface' : 'border-border bg-surface')}>
              <span className="flex items-center gap-2 text-ui font-medium text-fg">
                <span className="text-caption text-fg-subtle num">{formatInt(i + 1)}</span>
                {r.title}
              </span>
              <span className="text-caption text-fg-muted">{r.line}</span>
            </li>
          ))}
        </ol>
        <div className="flex flex-col gap-1">
          <span className="text-ui font-medium text-fg">What it can do</span>
          <span className="text-caption text-fg-muted">
            Anything you can do in the console, within your role: {byTier.map((x) => `${formatInt(x.n)} ${TIER_LABEL[x.t].toLowerCase()}`).join(', ')} capabilities, and any other route by path. Critical steps always wait for a person; it never changes its own settings.
          </span>
        </div>
      </div>
    </Panel>
  );
}

function SettingsBody({ settings }: { settings: AssistantSettings }) {
  const canEdit = useCan('admin');
  // The hub lets only an owner turn on full autonomy.
  const isOwner = useCan('owner');
  const machines = useMachines();
  const accounts = useLaunchAccounts();
  const [form, setForm] = useState<SettingsForm>(() => settingsToForm(settings));
  const [rules, setRules] = useState<Record<string, CapabilityRule>>(settings.rules);
  const [errors, setErrors] = useState<SettingsErrors>({});
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<UiError | null>(null);

  // When the hub's settings change (another tab, another person), start over from them.
  useEffect(() => {
    setForm(settingsToForm(settings));
    setRules(settings.rules);
    setErrors({});
    setTouched(false);
  }, [settings]);

  const saved = settingsToForm(settings);
  const formDirty = (Object.keys(form) as (keyof SettingsForm)[]).some((k) => form[k] !== saved[k]);
  const rulesDirty = !sameRules(rules, settings.rules);
  const dirty = formDirty || rulesDirty;
  const groups = useMemo(() => ruleGroups(rules, form.mode, 'owner', { harness: settings.harness }), [rules, form.mode, settings.harness]);
  const overrides = groups.reduce((n, g) => n + g.overrides, 0);

  const set = <K extends keyof SettingsForm>(k: K, v: SettingsForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setTouched(true);
    const { patch, errors: errs } = formToPatch(form, settings);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const full = rulesDirty ? { ...patch, rules } : patch;
    if (!Object.keys(full).length) return;
    setBusy(true);
    setSaveError(null);
    try {
      await saveAssistantSettings(full);
      toast('Assistant settings saved');
    } catch (e) {
      setSaveError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const discard = () => {
    setForm(settingsToForm(settings));
    setRules(settings.rules);
    setErrors({});
    setTouched(false);
    setSaveError(null);
  };

  const online = machines.filter((m) => m.online);
  const current = settings.machineId ? machines.find((m) => m.id === settings.machineId) : undefined;
  const machineOptions = [
    { value: '', label: 'Let Orgus pick' },
    ...online.map((m) => ({ value: m.id, label: m.name })),
    ...(current && !current.online ? [{ value: current.id, label: `${current.name} (offline)` }] : []),
    ...(settings.machineId && !current ? [{ value: settings.machineId, label: 'A machine this hub no longer knows' }] : []),
  ];
  const accountOptions = assistantAccountOptions(accounts.state, form.launchAccountId);
  const accountLabel = accountOptions.find((o) => o.value === form.launchAccountId)?.label ?? 'Workspace default';
  const models = AI_MODELS.some((m) => m.value === form.model) ? AI_MODELS : [...AI_MODELS, { value: form.model, label: form.model }];
  const shownErrors = touched ? errors : {};
  // Where it runs folds to one line; an error in it opens it.
  const [limitsOpen, setLimitsOpen] = useState(false);
  const limitsError = !!(shownErrors.model || shownErrors.budget || shownErrors.timeLimit || shownErrors.approvalWait);
  const budget = Number(form.budget);
  const limitsSummary = [
    accountLabel,
    models.find((m) => m.value === form.model)?.label ?? form.model,
    machineOptions.find((m) => m.value === form.machineId)?.label ?? 'Let Orgus pick',
    Number.isFinite(budget) && form.budget.trim() ? `${formatMoney(budget)} and ${form.timeLimit} min a reply` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex flex-col gap-6">
      {!canEdit && (
        <p role="status" className="rounded-card border border-border bg-surface px-3 py-2 text-ui text-fg-muted">
          Only an owner or admin can change these settings. You can read them here.
        </p>
      )}

      <Panel title="Assistant" icon={<Bot />}>
        <div className="flex flex-col gap-6">
          <SettingSwitch field="enabled" settings={settings} canEdit={canEdit} />
          <SettingSwitch field="harness" settings={settings} canEdit={canEdit} />
        </div>
      </Panel>

      <RunsPanel settings={settings} />

      <Panel title="Autonomy" sub="For every conversation">
        <div className="flex max-w-(--layout-width-form) flex-col gap-3">
          <p className="text-caption text-fg-subtle">Never above the role of the person who asked.</p>
          <ModeCards value={form.mode} onChange={(m) => set('mode', m)} disabled={!canEdit} ownerOnlyLocked={!isOwner && settings.mode !== 'full'} />
        </div>
      </Panel>

      <Panel title="Where it runs and its limits" sub={limitsSummary} collapsible open={limitsOpen || limitsError} onOpenChange={setLimitsOpen}>
        <div className="grid max-w-(--layout-width-form) grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Account"
            value={form.launchAccountId}
            onValueChange={(v) => set('launchAccountId', v)}
            options={accountOptions}
            disabled={!canEdit || accounts.phase === 'loading'}
            hint={form.launchAccountId ? 'Replies and AI writing run on it; its key reaches the machine only for that run.' : defaultAccountLine(accounts.state)}
          />
          <Select label="Model" value={form.model} onValueChange={(v) => set('model', v)} options={models} disabled={!canEdit} error={shownErrors.model} hint="Claude Code's own default when empty." />
          <Select label="Machine" value={form.machineId} onValueChange={(v) => set('machineId', v)} options={machineOptions} disabled={!canEdit} hint="Orgus picks the hub's own machine first, else the least busy one, else starts a stopped cloud VM." />
          <Input label="Budget per reply (USD)" inputMode="decimal" value={form.budget} onChange={(e) => set('budget', e.target.value)} disabled={!canEdit} error={shownErrors.budget} hint="Claude Code stops a reply that would cost more." />
          <Input label="Time limit per reply (minutes)" inputMode="decimal" value={form.timeLimit} onChange={(e) => set('timeLimit', e.target.value)} disabled={!canEdit} error={shownErrors.timeLimit} hint="Approval waits count toward it." />
          <Input label="Approval wait (minutes)" inputMode="decimal" value={form.approvalWait} onChange={(e) => set('approvalWait', e.target.value)} disabled={!canEdit} error={shownErrors.approvalWait} hint="A step nobody answers in this time is not run." />
        </div>
      </Panel>

      <Panel title="Capability rules" sub={overrides ? `${formatInt(overrides)} set` : 'All default'} flush collapsible>
        <p className="px-4 pb-3 text-caption text-fg-subtle">Override the mode for one capability. Reads always run; the middle column is what {AUTONOMY_LABEL[form.mode]} does now.</p>
        <div data-list>
          {groups.map((g) => (
            <section key={g.id} aria-label={g.id === 'harness' ? g.label : `${g.label} capabilities`}>
              <div className="flex h-row items-center gap-3 border-b border-border-subtle bg-raised px-4" data-group-header>
                <span className="text-ui font-medium text-fg">{g.label}</span>
                <span className="min-w-0 flex-1 truncate text-caption text-fg-subtle">{g.description}</span>
                {g.overrides > 0 && <span className="shrink-0 text-caption text-fg-muted num">{formatInt(g.overrides)} set</span>}
              </div>
              <ul className="flex flex-col divide-y divide-border-subtle border-b border-border-subtle">
                {g.rows.map((r) => (
                  <RuleRowView key={r.key} row={r} disabled={!canEdit} onChange={(c) => setRules((x) => withRule(x, r.key, c))} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Panel>

      {canEdit && (dirty || saveError || shownErrors.form) && (
        <div className="sticky bottom-0 z-(--z-sticky) flex flex-col gap-2 rounded-card border border-border bg-raised px-4 py-3 shadow-overlay">
          {saveError && <ErrorState variant="inline" error={saveError} />}
          {shownErrors.form && (
            <p role="alert" className="text-ui text-status-danger-ink">
              {shownErrors.form}
            </p>
          )}
          <div className="flex items-center gap-3">
            <span className="mr-auto text-ui text-fg-muted">{dirty ? 'You have unsaved changes.' : 'Not saved.'}</span>
            <Button variant="ghost" onClick={discard} disabled={busy}>
              Discard changes
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void save()}>
              Save settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AssistantSettingsView() {
  const view = useAssistant();
  if (view.phase === 'loading') {
    return (
      <div className="flex flex-col gap-4" role="status" aria-label="Loading assistant settings">
        <Skeleton className="h-24 rounded-card" />
        <Skeleton className="h-64 rounded-card" />
      </div>
    );
  }
  if (view.phase === 'missing') {
    return (
      <ModuleAbsent
        icon={<PlugZap />}
        feature="The assistant"
        hub={{ title: 'This hub does not run the assistant', description: 'Update Orgus on the hub machine and restart it to talk to the assistant and set its autonomy here.' }}
        instead="Start sessions from Sessions and answer approvals and questions from the Inbox."
      />
    );
  }
  if (view.phase === 'error' || !view.state) return <ErrorState error={view.error} onRetry={view.retry} />;
  return <SettingsBody settings={view.state.settings} />;
}
